import {closeVersionHistoryPanel, onCopyStylesClicked, redo, undo, unlockAllLockedItemsInActiveSelection} from '@Components/poster-editor/library/poster-editor-library';
import {ElementDataType} from '@Libraries/add-media-library';
import {
  onOpenCropModal,
  onOpenDownloadModal,
  onOpenMaskingModal,
  onOpenResizeModal,
  onOpenStockAudioModal,
  onOpenStockMediaModal,
  onOpenUserMediaModal,
  openCollaboratorsModal,
} from '@Components/poster-editor/library/poster-editor-open-modals';
import {KeyboardShortcutScope} from '@Libraries/keyboard-shortcuts';
import hotkeys from 'hotkeys-js';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import {ResizeSelectionKey} from '@PosterWhiteboard/page/active-selection.class';
import {TextHorizontalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import type {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import type {TextSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {isModalOpen} from '@Components/modal-container';
import {isLocal} from '@Libraries/environment-library';
import type {NudgeValues} from '@/libraries/poster-item-keyboard-movement';
import {initPosterItemKeyboardMovement} from '@/libraries/poster-item-keyboard-movement';
import {updateAutoSaveTooltipState} from '@Components/poster-editor/poster-editor-reducer';
import {isEmbeddedMorphModeActive} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {noop} from '@Utils/general.util';

const SHORTCUTS = {
  PLAY_PAUSE_POSTER: 'space',
  MUTE: 'ctrl+m, command+m',
  REDO: 'ctrl+shift+z , ctrl+y, command+shift+z , command+y',
  UNDO: 'ctrl+z,command+z',
  ON_PLUS: '+,=',
  ON_MINUS: '-',
  ZOOM_IN: 'ctrl++,ctrl+=,command++,command+=',
  ZOOM_OUT: 'ctrl+-,command+-',
  DELETE: 'del, backspace',
  ADD_TEXT: 't',
  ENTER_DRAW_MODE: 'd',
  ADD_QR_CODE: 'q',
  TOGGLE_LOCK_MOVEMENT: 'ctrl+l,command+l',
  SELECT_NEXT_ITEM: 'tab',
  SELECT_PREVIOUS_ITEM: 'shift+tab',
  DESELECT: 'escape',
  RESIZE: 'shift+r',
  ADD_STOCK_PHOTO: 'shift+p',
  MEDIA_OR_MASKING: 'shift+m',
  ADD_UPLOADS_MEDIA: 'shift+u',
  ADD_STOCK_VIDEO_OR_FLIP_VERTICAL: 'shift+v',
  CUT: 'command+x,ctrl+x',
  TEST: 'command+alt+t',
  DUPLICATE: 'ctrl+d, command+d',
  CROP_OR_COLLABORATE: 'shift+c',
  ADD_STOCK_AUDIO: 'shift+a',
  SELECT_ALL_UNLOCKED: 'ctrl+a, command+a',
  SELECT_ALL: 'ctrl+shift+a, command+shift+a',
  DOWNLOAD: 'shift+d',
  ENTER: 'enter',
  SAVE: 'shift+s, ctrl+s, command+s',
  COPY: 'ctrl+c,command+c',
  ZOOM_TO_FIT: 'ctrl+0,command+0',
  FLIP_HORIZONTAL: 'shift+h',
  BACKWARD: 'ctrl+down, command+down',
  SEND_TO_BOTTOM: 'ctrl+shift+down, command+shift+down',
  FORWARD: 'ctrl+up, command+up',
  SEND_TO_TOP: 'ctrl+shift+up, command+shift+up',
  BOLD_OR_BLEED: 'ctrl+b,command+b',
  ITALICS: 'ctrl+i,command+i',
  UNDERLINE: 'ctrl+u,command+u',
  // conflicting with save
  // STRIKE_THROUGH: 'ctrl+s,command+s',
  TEXT_LEFT_ALIGN: 'ctrl+shift+l,command+shift+l',
  TEXT_RIGHT_ALIGN: 'ctrl+shift+r,command+shift+r',
  TEXT_CENTER_ALIGN: 'ctrl+shift+c,command+shift+c',
  TEXT_JUSTIFIED_ALIGN: 'ctrl+shift+j,command+shift+j',
  GRID: 'ctrl+g,command+g',
  COPY_STYLES: 'ctrl+alt+c, cmd+option+c',

  // Will be added once the editor modals task is implemented
  // FLIP_VERTICAL: 'shift+v',

  // Will be added once the editor modals task is implemented
  // PUBLISH: 'shift+p'
};

const VERSION_HISTORY_SHORTCUTS = {
  EXIT_VERSION_HISTORY: 'escape',
  PLAY_PAUSE_POSTER: 'space',
};

export const bindPosterEditorShortcuts = (): void => {
  hotkeys.setScope(KeyboardShortcutScope.POSTER_EDITOR);
  bindEditorShortcut(SHORTCUTS.PLAY_PAUSE_POSTER, onTogglePlayPause);
  bindEditorShortcut(SHORTCUTS.MUTE, onMute);
  bindEditorShortcut(SHORTCUTS.REDO, redo);
  bindEditorShortcut(SHORTCUTS.UNDO, undo);
  bindEditorShortcut(SHORTCUTS.ON_PLUS, onPlusOverloadKey);
  bindEditorShortcut(SHORTCUTS.ON_MINUS, onMinusOverloadKey);
  bindEditorShortcut(SHORTCUTS.ZOOM_IN, onZoomIn, KeyboardShortcutScope.DEFAULT);
  bindEditorShortcut(SHORTCUTS.ZOOM_OUT, onZoomOut, KeyboardShortcutScope.DEFAULT);
  bindEditorShortcut(SHORTCUTS.DELETE, onDelete);
  initPosterItemKeyboardMovement(KeyboardShortcutScope.POSTER_EDITOR, onNudgeItem, onNudgeItemEnd);
  bindEditorShortcut(SHORTCUTS.ADD_TEXT, addTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.BOLD_OR_BLEED, onBoldOrBleedOverloadKey);
  bindEditorShortcut(SHORTCUTS.GRID, onGridOverloadKey);
  bindEditorShortcut(SHORTCUTS.ITALICS, onItalicTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.UNDERLINE, onUnderlineTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.TEXT_LEFT_ALIGN, onLeftAlignTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.TEXT_RIGHT_ALIGN, onRightAlignTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.TEXT_CENTER_ALIGN, onCenterAlignTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.TEXT_JUSTIFIED_ALIGN, onJustifiedAlignTextOverloadKey);
  bindEditorShortcut(SHORTCUTS.ENTER_DRAW_MODE, onEnterDrawModeOverloadKey);
  bindEditorShortcut(SHORTCUTS.ADD_QR_CODE, addQrCodeOverloadKey);
  bindEditorShortcut(SHORTCUTS.SELECT_ALL_UNLOCKED, selectAllUnlockedItems);
  bindEditorShortcut(SHORTCUTS.SELECT_ALL, selectAllItems);
  bindEditorShortcut(SHORTCUTS.TOGGLE_LOCK_MOVEMENT, onToggleLockMovement);
  bindEditorShortcut(SHORTCUTS.SAVE, isEmbeddedMorphModeActive() ? noop : onSave);
  bindEditorShortcut(SHORTCUTS.SELECT_NEXT_ITEM, onSelectNextItem);
  bindEditorShortcut(SHORTCUTS.SELECT_PREVIOUS_ITEM, onSelectPreviousItem);
  bindEditorShortcut(SHORTCUTS.DESELECT, onDeselectItems);
  bindEditorShortcut(SHORTCUTS.RESIZE, onOpenResizeOverloadKey);
  bindEditorShortcut(SHORTCUTS.ENTER, onEnterOverloadKey);
  bindEditorShortcut(SHORTCUTS.DUPLICATE, onDuplicate);
  bindEditorShortcut(SHORTCUTS.MEDIA_OR_MASKING, onMediaOrMasking);
  bindEditorShortcut(SHORTCUTS.ADD_UPLOADS_MEDIA, onOpenUserMediaModal);
  bindEditorShortcut(SHORTCUTS.ADD_STOCK_VIDEO_OR_FLIP_VERTICAL, onFlipVerticalOverloadKey);
  bindEditorShortcut(SHORTCUTS.CROP_OR_COLLABORATE, onCropOrCollaborateOverloadKey);
  bindEditorShortcut(SHORTCUTS.ADD_STOCK_AUDIO, onAddStockAudioOverloadKey);
  bindEditorShortcut(SHORTCUTS.DOWNLOAD, onDownloadPosterOverloadKey);
  bindEditorShortcut(SHORTCUTS.COPY, onCopy);
  bindEditorShortcut(SHORTCUTS.ZOOM_TO_FIT, onZoomToFit);
  bindEditorShortcut(SHORTCUTS.FLIP_HORIZONTAL, onFlipHorizontalOverloadKey);
  bindEditorShortcut(SHORTCUTS.FORWARD, onMoveItemForwardOverloadKey);
  bindEditorShortcut(SHORTCUTS.BACKWARD, onMoveItemBackwardOverloadKey);
  bindEditorShortcut(SHORTCUTS.SEND_TO_BOTTOM, onMoveItemToBottomOverloadKey);
  bindEditorShortcut(SHORTCUTS.SEND_TO_TOP, onMoveItemToTopOverloadKey);
  bindEditorShortcut(SHORTCUTS.COPY_STYLES, onCopyStylesShortcutClicked);
  bindEditorShortcut(SHORTCUTS.CUT, onCutItem);
  if (window.PMW.currentUserOnPageLoad?.isSuper || isLocal()) {
    bindEditorShortcut(SHORTCUTS.TEST, (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      void onTestButton();
    });
  }

  document.body.addEventListener('wheel', onMouseWheel, {passive: false});
};

export const onTestButton2 = async (): Promise<void> => {
  const response = (await window.PMW.writeLocal('cart/switchOrderGenerationMode')) as boolean;
  console.log(`is new order generation on: ${response}`);
};

export const onTestButton = async (): Promise<void> => {
  const response = (await window.PMW.readLocal('cart/getOrderGenerationStatus')) as boolean;
  console.log(`is new order generation on: ${response}`);
};

export const unbindPosterEditorShortcuts = (): void => {
  hotkeys.deleteScope(KeyboardShortcutScope.POSTER_EDITOR);
  document.body.removeEventListener('wheel', onMouseWheel);
};

const onMouseWheel = (e: WheelEvent): void => {
  if (isModalOpen()) {
    return;
  }

  window.posterEditor?.whiteboard?.scaling.zoomOnWheelEvent(e);
};

export const enablePosterEditorShortcuts = (): void => {
  hotkeys.setScope(KeyboardShortcutScope.POSTER_EDITOR);
};

export const disablePosterEditorShortcuts = (): void => {
  if (hotkeys.getScope() === KeyboardShortcutScope.POSTER_EDITOR) {
    hotkeys.setScope(KeyboardShortcutScope.DEFAULT);
  }
};

export const bindVersionHistoryShortcuts = (): void => {
  bindEditorShortcut(VERSION_HISTORY_SHORTCUTS.EXIT_VERSION_HISTORY, onExitVersionHistory, KeyboardShortcutScope.VERSION_HISTORY);
  bindEditorShortcut(VERSION_HISTORY_SHORTCUTS.PLAY_PAUSE_POSTER, onTogglePlayPause, KeyboardShortcutScope.VERSION_HISTORY);
};

export const unbindVersionHistoryShortcuts = (): void => {
  hotkeys.deleteScope(KeyboardShortcutScope.VERSION_HISTORY);
};

export const enableVersionHistoryShortcuts = (): void => {
  hotkeys.setScope(KeyboardShortcutScope.VERSION_HISTORY);
  bindVersionHistoryShortcuts();
};

export const disableVersionHistoryShortcuts = (): void => {
  unbindVersionHistoryShortcuts();
};

const bindEditorShortcut = (key: string, callback: (e: Event) => void, scope = KeyboardShortcutScope.POSTER_EDITOR): void => {
  hotkeys(
    key,
    {
      scope,
      keydown: true,
      keyup: false,
    },
    (event) => {
      event.preventDefault();
      callback(event);
    }
  );
};

const onExitVersionHistory = (e: Event): void => {
  e.preventDefault();
  void closeVersionHistoryPanel();
};

const onEnterOverloadKey = (e: Event): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems || selectedItems.length === 0) {
    return;
  }

  if (selectedItems.length > 1) {
    return;
  }

  const activeItem = selectedItems[0];

  if (activeItem.isText() || activeItem.isSlideshow()) {
    // @ts-ignore()
    activeItem.enterEditMode(e);
  }
};

const onCopyStylesShortcutClicked = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();

  onCopyStylesClicked();
};

const onDuplicate = (): void => {
  window.posterEditor?.whiteboard?.getCurrentPage().activeSelection.onDuplicate();
};

const onOpenResizeOverloadKey = (): void => {
  onOpenResizeModal();
};

const onCutItem = (e: Event): void => {
  window.posterEditor?.whiteboard?.getCurrentPage().clipboard.copySelection(e);
  window.posterEditor?.whiteboard?.deleteSelection();
};

const onCopy = (e: Event): void => {
  window.posterEditor?.whiteboard?.getCurrentPage().clipboard.copySelection(e);
};

const onDeselectItems = (): void => {
  window.posterEditor?.whiteboard?.clearSelection();

  if (window.posterEditor?.whiteboard?.drawing.isDrawModeOn) {
    window.posterEditor?.whiteboard?.drawing.updateFromObject({
      isDrawModeOn: false,
    });
  }
};

const onSelectNextItem = (): void => {
  selectItem(false);
};

const onSelectPreviousItem = (): void => {
  selectItem(true);
};

const selectItem = (selectPreviousItem: boolean): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const items = currentPage?.fabricCanvas.getObjects();
  const currentlyActiveItems = currentPage?.activeSelection.getActiveObjects();
  if (!items || !currentlyActiveItems) {
    return;
  }

  let selectionIndex = selectPreviousItem ? items.length - 1 : 0;
  if (currentlyActiveItems.length === 0) {
    // @ts-ignore
    currentPage?.activeSelection.setActiveObject(items[selectionIndex]);
    return;
  }

  let currentlySelectedIndex = -1;
  for (let index = 0; index < items.length; index += 1) {
    if (items[index] === currentlyActiveItems[0]) {
      currentlySelectedIndex = index;
      break;
    }
  }

  if (currentlySelectedIndex === -1) {
    return;
  }

  if (!selectPreviousItem) {
    if (items.length > currentlySelectedIndex + 1) {
      selectionIndex = currentlySelectedIndex + 1;
    } else {
      selectionIndex = 0;
    }
  } else if (currentlySelectedIndex === 0) {
    selectionIndex = items.length - 1;
  } else {
    selectionIndex = currentlySelectedIndex - 1;
  }
  // @ts-ignore
  currentPage?.activeSelection.setActiveObject(items[selectionIndex]);
};

const onSave = (): void => {
  void window.posterEditor?.whiteboard?.savePoster.save();

  if (
    window.posterEditor?.whiteboard?.savePoster.isAutoSaveEnabled() &&
    !window.posterEditor.whiteboard.savePoster.hasUnsavedChanges() &&
    !window.PMW.redux.store.getState().posterEditor.showAutoSaveTooltip &&
    !window.PMW.redux.store.getState().posterEditor.haveShownAutoSaveTooltip &&
    window.PMW.redux.store.getState().userOnlineStatus.isUserOnline
  ) {
    window.PMW.redux.store.dispatch(updateAutoSaveTooltipState(true));

    setTimeout((): void => {
      window.PMW.redux.store.dispatch(updateAutoSaveTooltipState(false));
    }, 3000);
  }
};

const onToggleLockMovement = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const activeElements = currentPage.activeSelection.getActiveObjects();
  if (activeElements.length > 1) {
    unlockAllLockedItemsInActiveSelection();
  } else {
    const item = currentPage.getItemForFabricObject(activeElements[0]);
    void item?.updateLock(!item?.fabricObject.lockMovementX);
  }
};

const addTextOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  if (currentPage.activeSelection.getSelectedViews().length > 0) {
    return;
  }

  void currentPage.items.addItems.addTextItem({
    type: ElementDataType.TEXT,
  });
};

const onEnterDrawModeOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  if (currentPage.activeSelection.getSelectedViews().length > 0) {
    return;
  }

  window.posterEditor?.whiteboard?.drawing.updateFromObject({
    isDrawModeOn: !window.posterEditor.whiteboard.drawing.isDrawModeOn,
  });
};

const addQrCodeOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  if (currentPage.activeSelection.getSelectedViews().length > 0) {
    return;
  }

  void currentPage.items.addItems.addQRItem({
    type: ElementDataType.QR_CODE,
  });
};

const onNudgeItemEnd = (): void => {
  window.posterEditor?.whiteboard?.history.addPosterHistory();
};

const nudgeAudioItem = (nudgeValues: NudgeValues): void => {
  if (nudgeValues.x === 0) {
    return;
  }

  const audioItem = window.posterEditor?.whiteboard?.audioClips.getSelectedAudioItem();
  const pageDuration = window.posterEditor?.whiteboard?.getCurrentPage().getDuration();

  if (!audioItem || !pageDuration) {
    return;
  }

  let newOnPosterStartTime = audioItem.onPosterStartTime + nudgeValues.x / 10;

  if (newOnPosterStartTime < 0) {
    newOnPosterStartTime = 0;
  }

  if (newOnPosterStartTime + audioItem.audioPlayer.getTrimmedPlaybackDuration() > pageDuration) {
    newOnPosterStartTime = pageDuration - audioItem.audioPlayer.getTrimmedPlaybackDuration();
  }

  void audioItem.updateFromObject({
    onPosterStartTime: newOnPosterStartTime,
  });
};

const onNudgeItem = (nudgeValues: NudgeValues): void => {
  const items = window.posterEditor?.whiteboard?.getSelectedItems();
  if (!items || items.length === 0) {
    if (window.posterEditor?.whiteboard?.audioClips.getSelectedAudioItem()) {
      nudgeAudioItem(nudgeValues);
    }
    return;
  }
  const isGroup = items.length > 1;

  if (isGroup) {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }
    const group = currentPage.activeSelection.getActiveObject();
    if (!group) {
      return;
    }

    if (group.lockMovementX) {
      return;
    }

    currentPage.activeSelection
      .updateGroupSelection(
        {
          left: group.left + nudgeValues.x,
          top: group.top + nudgeValues.y,
        },
        false
      )
      .catch((e) => {
        console.error(e);
      });
  } else {
    const activeItem = window.posterEditor?.whiteboard?.getItemForId(items[0].uid);
    if (!activeItem) {
      return;
    }

    if (activeItem?.fabricObject.lockMovementX) {
      return;
    }

    void activeItem.updateFromObject(
      {
        left: activeItem.fabricObject.getX() + nudgeValues.x,
        top: activeItem.fabricObject.getY() + nudgeValues.y,
      },
      {
        undoable: false,
      }
    );
  }
};

const selectAllUnlockedItems = (): void => {
  window.posterEditor?.whiteboard?.getCurrentPage().selectAllUnlockedItems();
};

const selectAllItems = (): void => {
  window.posterEditor?.whiteboard?.getCurrentPage().selectAllItems();
};

const onPlusOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  if (window.posterEditor?.whiteboard?.hasActiveSelection()) {
    window.posterEditor.whiteboard.getCurrentPage().activeSelection.resizeActiveSelection(e, ResizeSelectionKey.UP);
  } else {
    window.posterEditor?.whiteboard?.scaling.zoomIn();
  }
};

const onZoomIn = (e: Event): void => {
  e.stopPropagation();
  window.posterEditor?.whiteboard?.scaling.zoomIn();
};

const onZoomOut = (e: Event): void => {
  e.stopPropagation();
  window.posterEditor?.whiteboard?.scaling.zoomOut();
};

const onMinusOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  if (window.posterEditor?.whiteboard?.hasActiveSelection()) {
    window.posterEditor.whiteboard.getCurrentPage().activeSelection.resizeActiveSelection(e, ResizeSelectionKey.DOWN);
  } else {
    window.posterEditor?.whiteboard?.scaling.zoomOut();
  }
};

const onTogglePlayPause = (): void => {
  window.posterEditor?.whiteboard?.pages.poster.togglePlayPause();
};

const onMute = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const selectedItems = currentPage?.getSelectedItems();

  if (!currentPage?.isSingleItemSelected() || !selectedItems) {
    return;
  }

  const activeItem = selectedItems[0];
  if (activeItem?.isVideo()) {
    void activeItem.updateFromObject({
      volume: activeItem.isMuted() ? 1 : 0,
    });
  }
};

const onDelete = (): void => {
  window.posterEditor?.whiteboard?.deleteSelection();
};

const onCropOrCollaborateOverloadKey = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems || (selectedItems && selectedItems.length === 0)) {
    openCollaboratorsModal();
    return;
  }

  onCropOverloadKey();
};

const onMediaOrMasking = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems || (selectedItems && selectedItems.length !== 1)) {
    onOpenStockMediaModal();
    return;
  }
  if (selectedItems[0]?.isImage()) {
    void onOpenMaskingModal(selectedItems[0]);
  }
  if (selectedItems[0]?.isSlideshow()) {
    void onOpenMaskingModal(selectedItems[0].getSelectedSlide() as ImageSlideItem);
  }
};

const onCropOverloadKey = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems || (selectedItems && selectedItems.length !== 1)) {
    return;
  }
  if (selectedItems[0]?.isImage()) {
    onOpenCropModal(selectedItems[0]);
  }
  if (selectedItems[0]?.isSlideshow()) {
    onOpenCropModal(selectedItems[0].getSelectedSlide() as ImageSlideItem);
  }
};

const onAddStockAudioOverloadKey = (): void => {
  onOpenStockAudioModal();
};

const onDownloadPosterOverloadKey = (): void => {
  if (window.posterEditor?.whiteboard?.isInitialized) {
    onOpenDownloadModal();
  }
};

const onZoomToFit = (): void => {
  window.posterEditor?.whiteboard?.scaling.fitToScreen();
};

const onFlipVerticalOverloadKey = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems || (selectedItems && selectedItems.length !== 1)) {
    return;
  }

  const activeItem = selectedItems[0];
  if (activeItem.isTab() || activeItem.isText() || activeItem.isTableItem() || activeItem.isMenu()) {
    return;
  }

  void activeItem.updateFromObject({
    flipY: !activeItem.fabricObject.flipY,
  });
};

const onFlipHorizontalOverloadKey = (): void => {
  const selectedItems = window.posterEditor?.whiteboard?.getCurrentPage().getSelectedItems();
  if (!selectedItems || (selectedItems && selectedItems.length !== 1)) {
    return;
  }

  const activeItem = selectedItems[0];
  if (activeItem.isTab() || activeItem.isText() || activeItem.isTableItem() || activeItem.isMenu()) {
    return;
  }

  void activeItem.updateFromObject({
    flipX: !activeItem.fabricObject.flipX,
  });
};

const onMoveItemForwardOverloadKey = (e: Event): void => {
  e.preventDefault();

  if (!window.posterEditor?.whiteboard?.getCurrentPage().items.canSelectedItemsBeMovedForward()) {
    return;
  }

  window.posterEditor?.whiteboard?.getCurrentPage().items.bringForwardSelectedItem();
};

const onMoveItemBackwardOverloadKey = (e: Event): void => {
  e.preventDefault();

  if (!window.posterEditor?.whiteboard?.getCurrentPage().items.canSelectedItemsBeMovedBackwards()) {
    return;
  }

  window.posterEditor?.whiteboard?.getCurrentPage().items.sendBackwardsSelectedItem();
};

const onMoveItemToBottomOverloadKey = (e: Event): void => {
  e.preventDefault();

  if (!window.posterEditor?.whiteboard?.getCurrentPage().items.canSelectedItemsBeMovedBackwards()) {
    return;
  }

  window.posterEditor?.whiteboard?.getCurrentPage().items.sendToBackSelectedItem();
};

const onMoveItemToTopOverloadKey = (e: Event): void => {
  e.preventDefault();

  if (!window.posterEditor?.whiteboard?.getCurrentPage().items.canSelectedItemsBeMovedForward()) {
    return;
  }

  window.posterEditor?.whiteboard?.getCurrentPage().items.bringToFrontSelectedItem();
};

const canTextStylesBeApplied = (): boolean => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const selectedItems = currentPage?.getSelectedItems();

  if (!currentPage?.isSingleItemSelected() || !selectedItems) {
    return true;
  }

  if (selectedItems[0].isText()) {
    return true;
  }

  return !!(selectedItems[0].isSlideshow() && selectedItems[0].getSelectedSlide().isTextSlide());
};

const onBoldOrBleedOverloadKey = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const selectedItems = currentPage?.getSelectedItems();

  if (!selectedItems || (selectedItems && selectedItems.length === 0)) {
    onBleedOverloadKey();
    return;
  }

  onBoldTextOverloadKey();
};

const onBleedOverloadKey = (): void => {
  const bleed = window.posterEditor?.whiteboard?.bleed;
  if (!bleed) {
    return;
  }
  void window.posterEditor?.whiteboard?.updateFromObject(
    {
      bleed: {
        enabled: !bleed.enabled,
      },
    },
    {
      undoable: false,
    }
  );
};

const onGridOverloadKey = (): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const selectedItems = currentPage?.getSelectedItems();

  if (!selectedItems || (selectedItems && selectedItems.length !== 0)) {
    return;
  }

  const grid = window.posterEditor?.whiteboard?.grid;
  if (!grid) {
    return;
  }
  void window.posterEditor?.whiteboard?.updateFromObject(
    {
      grid: {
        enabled: !grid.enabled,
      },
    },
    {
      undoable: false,
    }
  );
};

const onBoldTextOverloadKey = (): void => {
  if (!canTextStylesBeApplied()) {
    return;
  }

  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }
  if (activeItem.isText()) {
    if (activeItem.textStyles.stroke) {
      return;
    }

    void activeItem.updateFromObject({
      textStyles: {
        isBold: !activeItem.textStyles.isBold,
      },
    });
  }

  if (activeItem.isSlideshow()) {
    const selectedSlide = activeItem.getSelectedSlide();
    if (!selectedSlide.isTextSlide()) {
      return;
    }

    if (selectedSlide.textStyles.stroke) {
      return;
    }

    void activeItem.updateSlideFromObject(selectedSlide.uid, {
      textStyles: {
        isBold: !selectedSlide.textStyles.isBold,
      },
    });
  }
};
const onItalicTextOverloadKey = (): void => {
  if (!canTextStylesBeApplied()) {
    return;
  }

  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }
  if (activeItem.isText()) {
    void activeItem.updateFromObject({
      textStyles: {
        isItalic: !activeItem.textStyles.isItalic,
      },
    });
  }

  if (activeItem.isSlideshow()) {
    const selectedSlide = activeItem.getSelectedSlide();
    if (!selectedSlide.isTextSlide()) {
      return;
    }

    void activeItem.updateSlideFromObject(selectedSlide.uid, {
      textStyles: {
        isItalic: !selectedSlide.textStyles.isItalic,
      },
    });
  }
};
const onUnderlineTextOverloadKey = (): void => {
  if (!canTextStylesBeApplied()) {
    return;
  }

  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }
  if (activeItem.isText()) {
    void activeItem.updateFromObject({
      textStyles: {
        underLine: !activeItem.textStyles.underLine,
      },
    });
  }

  if (activeItem.isSlideshow()) {
    const selectedSlide = activeItem.getSelectedSlide();
    if (!selectedSlide.isTextSlide()) {
      return;
    }

    void activeItem.updateSlideFromObject(selectedSlide.uid, {
      textStyles: {
        underLine: !selectedSlide.textStyles.underLine,
      },
    });
  }
};

const onAlignTextItem = (activeItem: TextItem, textAlign: TextHorizontalAlignType): void => {
  if (activeItem.textStyles.textAlign === textAlign) {
    return;
  }

  void activeItem.updateFromObject({
    textStyles: {
      textAlign,
    },
  });
};

const onAlignTextSlideItem = (activeItem: SlideshowItem, activeSlide: TextSlideItem, textAlign: TextHorizontalAlignType): void => {
  if (activeSlide.textStyles.textAlign === textAlign) {
    return;
  }

  void activeItem.updateSlideFromObject(activeSlide.uid, {
    textStyles: {
      textAlign,
    },
  });
};

const onLeftAlignTextOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  if (!canTextStylesBeApplied()) {
    return;
  }
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }

  if (activeItem.isText()) {
    onAlignTextItem(activeItem, TextHorizontalAlignType.LEFT);
    return;
  }

  if (activeItem.isSlideshow()) {
    const activeSlide = activeItem.getSelectedSlide();
    if (activeSlide.isTextSlide()) {
      onAlignTextSlideItem(activeItem, activeSlide, TextHorizontalAlignType.LEFT);
    }
  }
};
const onRightAlignTextOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  if (!canTextStylesBeApplied()) {
    return;
  }
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }

  if (activeItem.isText()) {
    onAlignTextItem(activeItem, TextHorizontalAlignType.RIGHT);
    return;
  }

  if (activeItem.isSlideshow()) {
    const activeSlide = activeItem.getSelectedSlide();
    if (activeSlide.isTextSlide()) {
      onAlignTextSlideItem(activeItem, activeSlide, TextHorizontalAlignType.RIGHT);
    }
  }
};
const onCenterAlignTextOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  if (!canTextStylesBeApplied()) {
    return;
  }
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }

  if (activeItem.isText()) {
    onAlignTextItem(activeItem, TextHorizontalAlignType.CENTER);
    return;
  }

  if (activeItem.isSlideshow()) {
    const activeSlide = activeItem.getSelectedSlide();
    if (activeSlide.isTextSlide()) {
      onAlignTextSlideItem(activeItem, activeSlide, TextHorizontalAlignType.CENTER);
    }
  }
};
const onJustifiedAlignTextOverloadKey = (e: Event): void => {
  e.preventDefault();
  e.stopPropagation();
  if (!canTextStylesBeApplied()) {
    return;
  }
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeItem = currentPage?.getSelectedItems()[0];

  if (!activeItem) {
    return;
  }

  if (activeItem.isText()) {
    onAlignTextItem(activeItem, TextHorizontalAlignType.JUSTIFY);
    return;
  }

  if (activeItem.isSlideshow()) {
    const activeSlide = activeItem.getSelectedSlide();
    if (activeSlide.isTextSlide()) {
      onAlignTextSlideItem(activeItem, activeSlide, TextHorizontalAlignType.JUSTIFY);
    }
  }
};
